import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useConfig } from 'wagmi';
import { formatUnits, type Address } from 'viem';
import { getBalance } from 'viem/actions';
import { ink } from '../../utils/chains';
import { getRelayTokenPrice } from '../../hooks/useRelayPrice';
import useSolanaBalances from '../../hooks/useSolanaBalances';
import { getInvestorByName } from '../../utils/supabase';
import { sellInvestorToken, unwrapInvestorWsol, withdrawInvestorSol } from '../../utils/investorBotApi';
import {
  fetchRelayWalletTokens,
  fetchSupabaseEvmTokenMeta,
  fetchSupabaseSolTokenMeta,
  type RelayWalletToken
} from '../../utils/investorPortfolio';

const Page = styled.div`
  padding: 24px 20px 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #333333;
  background: #111111;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
    background: #1a1a1a;
  }
`;

const RefreshIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 16px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Value = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
`;

const ValueGreen = styled(Value)`
  color: #10b981;
`;

const Label = styled.div`
  font-size: 12px;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LabelGreen = styled(Label)`
  color: #10b981;
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: #71717a;
`;

const LoadingText = styled.div`
  font-size: 13px;
  color: #a1a1aa;
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #222222;

  &:last-child {
    border-bottom: none;
  }
`;

const TokenLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #111111;
  border: 1px solid #333333;
  object-fit: cover;
`;

const TokenFallback = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #111111;
  border: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #a1a1aa;
  font-weight: 700;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenName = styled.div`
  font-size: 14px;
  color: #ffffff;
  font-weight: 600;
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  color: #71717a;
`;

const TokenValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 13px;
  color: #ffffff;
  font-weight: 600;
`;

const TokenActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
`;

const ActionButton = styled.button`
  border-radius: 10px;
  border: 1px solid #333333;
  background: #111111;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
    background: #1a1a1a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TokenAmount = styled.div`
  font-size: 12px;
  color: #71717a;
  font-weight: 500;
`;

const ActionMessage = styled.div<{ $variant: 'success' | 'error' }>`
  font-size: 12px;
  color: ${({ $variant }) => ($variant === 'success' ? '#10b981' : '#ef4444')};
`;

const ActionLink = styled.a`
  color: inherit;
  text-decoration: underline;
  word-break: break-word;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
`;

const ETH_NATIVE_CHAINS = [
  { chainId: ink.id, name: 'Ink' },
  { chainId: 1, name: 'Ethereum' },
  { chainId: 8453, name: 'Base' },
  { chainId: 10, name: 'Optimism' },
  { chainId: 42161, name: 'Arbitrum' },
];

interface DisplayToken {
  slotIndex: number;
  name: string;
  symbol: string;
  logo?: string;
  balance: string;
  valueUSD: number;
  mintAddress: string;
  chainType: 'evm' | 'solana' | 'unknown';
  decimals?: number;
}

const InvestorDashboardPWA: React.FC = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const investorName = localStorage.getItem('investorName') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ethHoldings, setEthHoldings] = useState(0);
  const [solHoldings, setSolHoldings] = useState(0);
  const [wsolHoldings, setWsolHoldings] = useState(0);
  const [wsolBalanceRaw, setWsolBalanceRaw] = useState<bigint>(BigInt(0));
  const [tokenHoldings, setTokenHoldings] = useState<DisplayToken[]>([]);
  const [investorAddresses, setInvestorAddresses] = useState<{ evm?: string | null; svm?: string | null }>({});
  const [evmWalletTokens, setEvmWalletTokens] = useState<RelayWalletToken[]>([]);
  const [funding, setFunding] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; message: string; link?: string } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { balances: solBalances, loading: solLoading, refetch: refetchSolana } = useSolanaBalances(
    !!investorAddresses.svm,
    investorAddresses.svm || undefined
  );

  useEffect(() => {
    if (!investorName) {
      navigate('/investor-pwa', { replace: true });
    }
  }, [navigate, investorName]);

  useEffect(() => {
    const fetchInvestor = async () => {
      if (!investorName) return;
      setLoading(true);
      setError('');

      const investorResult = await getInvestorByName(investorName);
      if (!investorResult.success || !investorResult.investor) {
        setError(investorResult.error || 'Failed to load investor profile.');
        setLoading(false);
        return;
      }

      const investor = investorResult.investor;
      setInvestorAddresses({
        evm: investor.evm_address || null,
        svm: investor.svm_address || null,
      });
      setFunding(typeof investor.funding === 'number' ? investor.funding : null);

      setLoading(false);
    };

    fetchInvestor();
  }, [investorName, refreshKey]);

  useEffect(() => {
    const fetchEthHoldings = async () => {
      if (!investorAddresses.evm) {
        setEthHoldings(0);
        return;
      }

      try {
        let total = 0;
        const address = investorAddresses.evm as Address;

        await Promise.all(
          ETH_NATIVE_CHAINS.map(async ({ chainId }) => {
            try {
              const client = config.getClient({ chainId });
              if (!client) return;
              const nativeBalance = await getBalance(client, { address });
              if (nativeBalance === 0n) return;
              const price = await getRelayTokenPrice('0x0000000000000000000000000000000000000000', chainId);
              const amount = parseFloat(formatUnits(nativeBalance, 18));
              if (price) {
                total += amount * price;
              }
            } catch (err) {
              console.warn('[Investor] Failed to fetch native balance for chain', chainId, err);
            }
          })
        );

        setEthHoldings(total);
      } catch (err) {
        console.warn('[Investor] Failed to fetch ETH holdings', err);
        setEthHoldings(0);
      }
    };

    fetchEthHoldings();
  }, [config, investorAddresses.evm, refreshKey]);

  useEffect(() => {
    const fetchWalletTokens = async () => {
      if (!investorAddresses.evm) {
        setEvmWalletTokens([]);
        return;
      }

      const tokens = await fetchRelayWalletTokens(
        investorAddresses.evm,
        [ink.id, 1, 8453, 10, 42161],
        false
      );
      setEvmWalletTokens(tokens);
    };

    fetchWalletTokens();
  }, [investorAddresses.evm, refreshKey]);

  useEffect(() => {
    const buildHoldings = async () => {
      if (!investorAddresses.evm && !investorAddresses.svm) {
        setTokenHoldings([]);
        return;
      }

      const evmAddresses = evmWalletTokens.map((token) => token.address);
      const solMints = solBalances.filter((token) => !token.isNative).map((token) => token.mint);

      const [evmMeta, solMeta] = await Promise.all([
        fetchSupabaseEvmTokenMeta(evmAddresses),
        fetchSupabaseSolTokenMeta(solMints)
      ]);

      const displayTokens: DisplayToken[] = [];

      evmWalletTokens.forEach((token, index) => {
        if (token.balanceRaw <= 0n) return;
        const meta = evmMeta.get(token.address.toLowerCase());
        if (!meta) return;

        const decimals = meta.decimals ?? token.decimals ?? 18;
        const balance = token.balance.toString();
        const valueUSD = token.valueUSD ?? 0;

        displayTokens.push({
          slotIndex: index,
          name: meta.name,
          symbol: meta.symbol,
          logo: meta.logo || token.logo,
          balance,
          valueUSD,
          mintAddress: token.address,
          chainType: 'evm',
          decimals
        });
      });

      solBalances
        .filter((token) => !token.isNative && token.balanceRaw > 0n)
        .forEach((token, index) => {
          const meta = solMeta.get(token.mint);
          if (!meta) return;

          displayTokens.push({
            slotIndex: evmWalletTokens.length + index,
            name: meta.name,
            symbol: meta.symbol,
            logo: meta.logo || token.logo,
            balance: token.balance,
            valueUSD: token.valueUSD || 0,
            mintAddress: token.mint,
            chainType: 'solana',
            decimals: meta.decimals ?? token.decimals
          });
        });

      displayTokens.sort((a, b) => b.valueUSD - a.valueUSD);
      setTokenHoldings(displayTokens);
    };

    buildHoldings();
  }, [evmWalletTokens, solBalances, investorAddresses, refreshKey]);

  const totalTokenValue = useMemo(() => {
    return tokenHoldings.reduce((sum, token) => sum + token.valueUSD, 0);
  }, [tokenHoldings]);

  useEffect(() => {
    const solNative = solBalances.find((token) => token.isNative);
    setSolHoldings(solNative?.valueUSD || 0);
    const wsol = solBalances.find((token) => token.mint === 'So11111111111111111111111111111111111111112');
    setWsolHoldings(wsol?.valueUSD || 0);
    setWsolBalanceRaw(wsol?.balanceRaw || BigInt(0));
  }, [solBalances]);

  const totalPortfolioValue = useMemo(() => {
    return ethHoldings + solHoldings + totalTokenValue;
  }, [ethHoldings, solHoldings, totalTokenValue]);

  const roiPercent = useMemo(() => {
    if (!funding || funding <= 0) return null;
    return (totalPortfolioValue / funding) * 100;
  }, [funding, totalPortfolioValue]);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTokenAmount = (value: string) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isLoading = loading || solLoading;
  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    refetchSolana();
  };

  const handleSellToken = async (token: DisplayToken) => {
    setActionMessage(null);
    const sessionToken = localStorage.getItem('investorSessionToken') || '';
    if (!sessionToken) {
      setActionMessage({ type: 'error', message: 'Trading session not available. Please log in again.' });
      return;
    }

    const amount = window.prompt(`Sell how many ${token.symbol}?`);
    if (!amount || Number(amount) <= 0) return;

    setActionLoading(`sell-${token.slotIndex}`);
    const result = await sellInvestorToken({
      token: sessionToken,
      mintAddress: token.mintAddress,
      amount,
      decimals: token.decimals ?? 9,
    });
    setActionLoading(null);

    if (!result.success) {
      setActionMessage({ type: 'error', message: result.error || 'Swap failed.' });
      return;
    }

    const signature = result.data?.signature as string | undefined;
    const link = signature ? `https://solscan.io/tx/${signature}` : undefined;
    setActionMessage({
      type: 'success',
      message: `Sold ${token.symbol}.`,
      link
    });
    handleRefresh();
  };

  const handleWithdrawSol = async () => {
    setActionMessage(null);
    const sessionToken = localStorage.getItem('investorSessionToken') || '';
    if (!sessionToken) {
      setActionMessage({ type: 'error', message: 'Trading session not available. Please log in again.' });
      return;
    }

    const destination = window.prompt('Enter destination SOL address');
    if (!destination) return;

    const amount = window.prompt('Enter SOL amount (or type "all")');
    if (!amount) return;

    const withdrawAll = amount.trim().toLowerCase() === 'all';
    const amountSol = withdrawAll ? undefined : amount;

    setActionLoading('withdraw-sol');
    const result = await withdrawInvestorSol({
      token: sessionToken,
      toAddress: destination,
      amountSol,
      withdrawAll
    });
    setActionLoading(null);

    if (!result.success) {
      setActionMessage({ type: 'error', message: result.error || 'Withdraw failed.' });
      return;
    }

    const signature = result.data?.signature as string | undefined;
    const link = signature ? `https://solscan.io/tx/${signature}` : undefined;
    setActionMessage({
      type: 'success',
      message: 'Withdraw sent.',
      link
    });
    handleRefresh();
  };

  const handleUnwrapWsol = async () => {
    setActionMessage(null);
    const sessionToken = localStorage.getItem('investorSessionToken') || '';
    if (!sessionToken) {
      setActionMessage({ type: 'error', message: 'Trading session not available. Please log in again.' });
      return;
    }

    setActionLoading('unwrap-wsol');
    const result = await unwrapInvestorWsol(sessionToken);
    setActionLoading(null);

    if (!result.success) {
      setActionMessage({ type: 'error', message: result.error || 'Unwrap failed.' });
      return;
    }

    const signature = result.data?.signature as string | undefined;
    const link = signature ? `https://solscan.io/tx/${signature}` : undefined;
    setActionMessage({ type: 'success', message: 'WSOL unwrapped.', link });
    handleRefresh();
  };

  return (
    <Page>
      <SectionHeader>
        <SectionTitle>Portfolio View</SectionTitle>
        <RefreshButton onClick={handleRefresh} aria-label="Refresh balances">
          <RefreshIcon src="/sentry-wallet-refresh.png" alt="Refresh" />
          Refresh
        </RefreshButton>
      </SectionHeader>
      <Card>
        <Label>Total Portfolio Value</Label>
        {isLoading ? <LoadingText>Loading...</LoadingText> : <ValueGreen>${formatCurrency(totalPortfolioValue)}</ValueGreen>}
        <Label>Amount Managed</Label>
        {isLoading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <Value>${formatCurrency(funding || 0)}</Value>
        )}
        <LabelGreen>ROI to Date</LabelGreen>
        {isLoading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <ValueGreen>{roiPercent === null ? '—' : `${roiPercent.toFixed(2)}%`}</ValueGreen>
        )}
      </Card>
      <Card>
        <Label>ETH Holdings</Label>
        {isLoading ? <LoadingText>Loading...</LoadingText> : <Value>${ethHoldings.toFixed(2)}</Value>}
      </Card>
      <Card>
        <Label>SOL Holdings</Label>
        {isLoading ? <LoadingText>Loading...</LoadingText> : <Value>${solHoldings.toFixed(2)}</Value>}
        <TokenActions>
          <ActionButton
            type="button"
            onClick={handleWithdrawSol}
            disabled={!investorAddresses.svm || isLoading || actionLoading === 'withdraw-sol'}
          >
            {actionLoading === 'withdraw-sol' ? 'Sending...' : 'Withdraw SOL'}
          </ActionButton>
        </TokenActions>
      </Card>
      <Card>
        <Label>WSOL Holdings</Label>
        {isLoading ? <LoadingText>Loading...</LoadingText> : <Value>${wsolHoldings.toFixed(2)}</Value>}
        <TokenActions>
          <ActionButton
            type="button"
            onClick={handleUnwrapWsol}
            disabled={!investorAddresses.svm || isLoading || wsolBalanceRaw === BigInt(0) || actionLoading === 'unwrap-wsol'}
          >
            {actionLoading === 'unwrap-wsol' ? 'Unwrapping...' : 'Unwrap WSOL'}
          </ActionButton>
        </TokenActions>
      </Card>

      <SectionTitle>Token Holdings</SectionTitle>
      <Card>
        {error && <ErrorText>{error}</ErrorText>}
        {actionMessage && (
          <ActionMessage $variant={actionMessage.type}>
            {actionMessage.message}
            {actionMessage.link && (
              <>
                {' '}
                <ActionLink href={actionMessage.link} target="_blank" rel="noopener noreferrer">
                  View on Solscan
                </ActionLink>
              </>
            )}
          </ActionMessage>
        )}
        {isLoading && !error && <LoadingText>Loading token balances...</LoadingText>}
        {!isLoading && tokenHoldings.length === 0 && <EmptyState>No tracked tokens yet.</EmptyState>}
        {!isLoading && tokenHoldings.length > 0 && (
          <>
            {tokenHoldings.map((token) => (
              <TokenRow key={`${token.slotIndex}-${token.symbol}`}>
                <TokenLeft>
                  {token.logo ? (
                    <TokenLogo src={token.logo} alt={token.symbol} />
                  ) : (
                    <TokenFallback>{token.symbol.slice(0, 3)}</TokenFallback>
                  )}
                  <TokenInfo>
                    <TokenName>{token.name}</TokenName>
                    <TokenSymbol>{token.symbol}</TokenSymbol>
                  </TokenInfo>
                </TokenLeft>
                <TokenValue>
                  <span>${token.valueUSD.toFixed(2)}</span>
                  <TokenAmount>{formatTokenAmount(token.balance)}</TokenAmount>
                  {token.chainType === 'solana' && (
                    <TokenActions>
                      <ActionButton
                        type="button"
                        onClick={() => handleSellToken(token)}
                        disabled={isLoading || actionLoading === `sell-${token.slotIndex}`}
                      >
                        {actionLoading === `sell-${token.slotIndex}` ? 'Selling...' : 'Sell'}
                      </ActionButton>
                    </TokenActions>
                  )}
                </TokenValue>
              </TokenRow>
            ))}
          </>
        )}
      </Card>
    </Page>
  );
};

export default InvestorDashboardPWA;
